import React, { useContext, useEffect, useState } from "react";
import moment from "moment";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import DangerousIcon from "@mui/icons-material/Dangerous";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Checkbox,
  FormControlLabel,
  Paper,
  Stack,
  Typography
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  StyledContainer,
  StyledInnerContainer,
  StyledHeader,
  StyledHeaderText,
  StyledFallback
} from "./Notifications.styles";
import API from "api";
import Session from "helpers/session";
import {
  associateReadingType,
  toLastFirstName,
  toLocalTime
} from "helpers/variables";
import { useTranslation } from "react-i18next";
import { GlobalContext } from "context/Provider";

export default function Notifications() {
  const { t } = useTranslation();
  const { notifications, notificationsDispatch, member } =
    useContext(GlobalContext);
  const currentEncounter = window.sessionStorage.getItem("encounter")
    ? JSON.parse(
        Session.decrypt(window.sessionStorage.getItem("encounter") || "")
      )
    : "";
  const [hasNotifications, setHasNotifications] = useState<boolean>(true);
  const [list, setList] = useState<any>();
  const [checked, setChecked] = React.useState<any>({});
  const [expandedAccordian, setExpandedAccordian] = React.useState<any>({});

  const handleSubmit = () => {
    let submissionArray: any = [];
    for (let item in checked) {
      const memberId = item.split("_")[0];
      const noticeId = item.split("_")[1];
      const readingId = item.split("_")[2];
      if (memberId !== "s") {
        if (checked[item]) {
          submissionArray.push({
            noticeId,
            memberId,
            authorId: Session.crackCookieUserId("coordinator"),
            readingId,
            authorName: Session.crackCookieUsername("coordinator")
          });
        }
      }
    }
    API.kore.notes
      .create(submissionArray, Session.crackCookieToken("coordinator"))
      .then((noteRet) => {
        generateAlertList();
        setChecked({});
        setExpandedAccordian({});
      })
      .catch((err) => console.log(err));
  };

  const handleCheckSingle = (event: React.ChangeEvent<HTMLInputElement>) => {
    setChecked({
      ...checked,
      [`${event.target.id}_${event.target.name}`]:
        !checked[`${event.target.id}_${event.target.name}`]
    });
  };

  const handleCheckAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    let alertGroup: any = {};
    let i = 0;
    while (i < parseInt(event.target.name)) {
      i++;
      alertGroup[
        `${event.target.id}_${list[event.target.id][i - 1]._id}_${
          list[event.target.id][i - 1].linkedReading
        }_${i - 1}`
      ] = !checked[`s_${event.target.id}`];
    }
    setChecked({
      ...checked,
      ...alertGroup,
      [`s_${event.target.id}`]: !checked[`s_${event.target.id}`]
    });
  };

  const handleExpand = (props: any) => {
    setExpandedAccordian({
      ...expandedAccordian,
      [props]: !expandedAccordian[props]
    });
  };

  const generateAlertList = () => {
    API.kore.notifications
      .getList(Session.crackCookieToken("coordinator"), "acknowledged:false")
      .then((notices: any) => {
        if (notices.items.length < 1) {
          setHasNotifications(false);
          notificationsDispatch({
            type: "UPDATE_COUNT",
            payload: 0
          });
        } else {
          const alertdictionary: any = {};
          notices.items.map((res: any) => {
            if (alertdictionary[res.memberId]) {
              alertdictionary[res.memberId].push(res);
            } else {
              alertdictionary[res.memberId] = [res];
            }
          });
          setList(alertdictionary);
          if (notifications?.itemsCount !== notices.items.length) {
            notificationsDispatch({
              type: "UPDATE_COUNT",
              payload: notices.items.length
            });
          }
        }
      });
  };

  const generateAccordians = () => {
    const accordianArr = [];
    let id = 0;
    for (let pat in list) {
      id++;
      accordianArr.push(
        <AlertAccordian
          patient={list[pat]}
          patientId={list[pat][0].memberId}
          name={list[pat][0].username}
          selected={checked}
        />
      );
    }
    let n = 0;
    while (n > id) {
      n++;
      setExpandedAccordian({ ...expandedAccordian, [`${id}`]: false });
    }
    return accordianArr;
  };

  const AlertAccordian = (props?: any) => {
    const { patientId, patient, name } = props;
    const generateAlertVitals = (values: any, limits: any, level: any) => {
      let alertValueArr = [];
      for (let value in values) {
        let valueDifference;
        let trendIcon;
        let direction;
        if (limits[value]) {
          if (limits[value].high < values[value]) {
            trendIcon = (
              <TrendingUpIcon color={level === "high" ? "error" : "warning"} />
            );
            valueDifference = (values[value] - limits[value].high).toFixed(2);
            direction = "Higher";
          } else if (limits[value].low > values[value]) {
            trendIcon = (
              <TrendingDownIcon
                color={level === "high" ? "error" : "warning"}
              />
            );
            valueDifference = limits[value].high - values[value];
            direction = "Lower";
          } else {
            trendIcon = (
              <CheckCircleIcon color={"success"} sx={{ pr: 1, pt: 0.5 }} />
            );
            valueDifference = "";
          }
          alertValueArr.push(
            <Stack
              direction="row"
              spacing={2}
              sx={{ border: `1px solid grey`, p: 0.5 }}
            >
              <b>{value}</b>
              <Stack spacing={2}>
                {values[value]}
                {trendIcon}
                {valueDifference !== ""
                  ? `${direction} by ${valueDifference}`
                  : ""}
              </Stack>
            </Stack>
          );
        }
      }
      return (
        <Stack direction={"row"} spacing={6}>
          {alertValueArr}
        </Stack>
      );
    };

    const AlertItem = (props: any) => {
      const { indexLocation, alerts, level, id, breach, reading } = props;
      return (
        <Paper>
          <Stack direction="row" alignItems={"center"} sx={{ p: 0.5 }}>
            <FormControlLabel
              label=""
              control={
                <Checkbox
                  name={`${breach}_${reading}_${indexLocation}`}
                  id={id}
                  checked={
                    checked[`${id}_${breach}_${reading}_${indexLocation}`]
                  }
                  onChange={handleCheckSingle}
                />
              }
            />
            <Typography sx={{ width: 160 }}>
              {associateReadingType(alerts.readingType)}
            </Typography>
            <Stack
              direction={"row"}
              spacing={6}
              justifyContent={"space-evenly"}
              alignItems={"center"}
            >
              <Stack direction={"row"} alignItems={"center"} spacing={2}>
                <Typography>Warning Level:</Typography>
                {level === "high" ? (
                  <DangerousIcon color="error" fontSize="large" />
                ) : (
                  <WarningAmberIcon color="warning" fontSize="large" />
                )}
              </Stack>
              <p>
                {moment(toLocalTime(alerts.receivedAt)).format(
                  "MM/DD/YYYY hh:mm A"
                )}
              </p>

              {generateAlertVitals(
                alerts.readingValue,
                alerts.threshold,
                level
              )}
            </Stack>
          </Stack>
        </Paper>
      );
    };

    return (
      <Accordion
        expanded={expandedAccordian[patientId]}
        sx={{ border: 1, backgroundColor: "#E7E7E7" }}
      >
        <AccordionSummary
          onClick={() => {
            handleExpand(patientId);
          }}
          expandIcon={<ExpandMoreIcon />}
          aria-controls={`panel-contents-${patientId}`}
          id={`panel-header-${patientId}`}
        >
          <Stack direction={"row"} alignItems={"center"}>
            <b>{toLastFirstName(name)}</b>
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          <FormControlLabel
            label="Select All"
            control={
              <Checkbox
                id={patientId}
                name={patient.length}
                onChange={handleCheckAll}
                checked={checked[`s_${patientId}`]}
              />
            }
          />
          <Stack spacing={0.8}>
            {patient.map((M: any, index: any) => {
              if (M.breachType === "high") {
                return (
                  <AlertItem
                    alerts={M}
                    indexLocation={index}
                    level={"high"}
                    id={patientId}
                    breach={M._id}
                    reading={M.linkedReading}
                  />
                );
              }
            })}
            {patient.map((M: any, index: any) => {
              if (M.breachType === "moderate") {
                return (
                  <AlertItem
                    alerts={M}
                    indexLocation={index}
                    level={"high"}
                    id={patientId}
                    breach={M._id}
                    reading={M.linkedReading}
                  />
                );
              }
            })}
          </Stack>
        </AccordionDetails>
      </Accordion>
    );
  };

  useEffect(() => {
    generateAlertList();
    if (currentEncounter && currentEncounter.actions.length !== 0) {
      const eventBody = {
        coordinatorId: Session.crackCookieUserId("coordinator"),
        coordinatorName: Session.crackCookieUsername("coordinator"),
        memberId: currentEncounter.memberId,
        memberName: currentEncounter.memberName.username,
        duration: member?.secondsCounter.toString() || "",
        escalatedDuration: 0,
        actions: currentEncounter.actions
      };
      API.kore.encounters
        .add(Session.crackCookieToken("coordinator"), eventBody)
        .then((en) => {
          window.sessionStorage.removeItem("encounter");
        });
    }
  }, []);

  return (
    <StyledContainer>
      <StyledHeader>
        <StyledHeaderText>{t("u-notifications")}</StyledHeaderText>
      </StyledHeader>

      {hasNotifications ? (
        <>
          <Button
            sx={{ marginTop: 2, marginLeft: 3 }}
            variant="contained"
            disabled={Object.keys(checked).length > 0 ? false : true}
            onClick={handleSubmit}
          >
            Acknowledge
          </Button>
          <StyledInnerContainer>{generateAccordians()}</StyledInnerContainer>
        </>
      ) : (
        <Stack
          direction={"column"}
          justifyContent={"center"}
          alignItems={"center"}
        >
          <h1>{"No notifications"}</h1>
          <StyledFallback></StyledFallback>
        </Stack>
      )}
    </StyledContainer>
  );
}
